import { Component, OnInit, ViewEncapsulation } from '@angular/core'
import { Title } from '@angular/platform-browser'
import { combineLatest, Observable } from 'rxjs'
import { filter, first, map, skip, switchMap, takeUntil } from 'rxjs/operators'
import { TranslateService } from '@ngx-translate/core'

import { AuthService } from './modules/auth/auth.service'
import { FeatureFlagService } from '@core/services/feature-flag.service'
import { SidebarService } from '@core/modules/sidebar/sidebar.service'
import { ThemeService } from '@shared/services/theme.service'
import { negativeBoolean } from '@core/helpers/negative-boolean'
import { PlatformSettings } from '@shared/models/platform-settings.model'
import { User } from '@shared/models/user.model'
import { DestroyService } from '@core/services/destroy.service'
import { PlatformSettingsService } from '@core/services/platfrom-settings.service'
import { NetworkStatusService } from '@core/services/network-status.service'
import { Theme } from '@shared/models/theme.model'
import { ClientStatus } from '@shared/models/client.model'
import { SettingsService } from './shared/common-components/settings/settings.service'
import { CrispService } from './modules/auth/crisp.service'
import { NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router'
import { applyV2AdminTheme, clearV2AdminTheme, isV2AdminUrl } from '@core/helpers/v2-admin-theme'

@Component({
  selector: 'lin-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [DestroyService, NetworkStatusService],
  standalone: false,
})
export class AppComponent implements OnInit {
  logoSrc: string | null = null
  favIcon: HTMLLinkElement = document.querySelector('#appIcon')
  platformSettings$: Observable<PlatformSettings>

  // The v1 app chrome (logo, sidebar, toolbar) rendered here is the shell for admin, reseller and the
  // legacy v1 client. A uiV2 client lives entirely in the self-contained /v2/client shell, so this
  // chrome must never render for them. Otherwise, while the v2 shell's lazy chunk loads (pronounced
  // on a slow connection or during impersonation, which enters at '/'), the v1 sidebar and its icon
  // set flash before the v2 rail replaces them. Gating render (not just CSS-hiding after the v2
  // component mounts) removes the flash at the source.
  readonly isLegacyChromeVisible$: Observable<boolean>

  get currentUser(): User {
    return this.authService.getCurrentUser()
  }

  get showBanner(): boolean {
    // const isGlobalBannerVisible = this.isGlobalMessageEnabled() // banner show even in logout

    if (!this.currentUser) {
      return this.isGlobalMessageEnabled() // show the global banner on the login screen too
    }

    const isClientStatusBannerVisible =
      this.currentUser.isClient() &&
      [ClientStatus.PartiallyBlockedBilling, ClientStatus.BlockedBilling].includes(this.currentUser?.team?.clientStatus) &&
      this.settingsService.areBillingFunctionalitiesEnabled

    const isGlobalBannerVisible = this.isGlobalMessageEnabled() // banner not show in logout
    return isClientStatusBannerVisible || isGlobalBannerVisible
  }

  private isGlobalMessageEnabled(): boolean {
    return false
  }

  constructor(
    private titleService: Title,
    public authService: AuthService,
    private sidebarService: SidebarService,
    private themeService: ThemeService,
    private readonly destroyService: DestroyService,
    private readonly platformSettingsService: PlatformSettingsService,
    private readonly settingsService: SettingsService,
    private crispService: CrispService,
    private router: Router,
    private readonly translate: TranslateService,
    private readonly featureFlagService: FeatureFlagService,
  ) {
    translate.setDefaultLang('en')

    this.isLegacyChromeVisible$ = combineLatest([
      this.authService.isLoggedIn$,
      this.authService.termsAccepted$,
      this.authService.currentUser$,
    ]).pipe(map(([isLoggedIn, termsAccepted, user]) => !!isLoggedIn && !!termsAccepted && !this.isV2Client(user)))

    this.initAdminTheme()
  }

  // The admin + reseller areas reskin the shared v1 chrome via `body.ui-v2-admin`. The lazy shell
  // (AdminComponent/ResellerComponent) used to add the class only after its chunk mounted, so on a
  // slow load the chrome painted in its old v1 style first and flashed. Driving the class from the
  // URL here applies it before first paint (fresh load / refresh) and on NavigationStart (before the
  // chunk + chrome repaint), and clears it once a navigation settles outside those areas.
  private initAdminTheme(): void {
    if (isV2AdminUrl(window.location.pathname)) applyV2AdminTheme()

    this.router.events.pipe(takeUntil(this.destroyService)).subscribe((event) => {
      // Apply as the navigation BEGINS (before the lazy shell chunk loads and the chrome repaints).
      if (event instanceof NavigationStart) {
        if (isV2AdminUrl(event.url)) applyV2AdminTheme()
        return
      }
      // Only reconcile on a SETTLED navigation (end / cancel / error), when `router.url` is the
      // resolved URL, so intermediate events mid-navigation can't clear a theme just applied, and a
      // cancelled or redirected navigation can never strand it on a non-admin page.
      const settled = event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError
      if (settled && !isV2AdminUrl(this.router.url)) clearV2AdminTheme()
    })
  }

  // A logged-in client on the v2 UI has no use for the v1 chrome; their whole app is the v2 shell.
  private isV2Client(user: User): boolean {
    return !!user?.isClient?.() && this.featureFlagService.uiV2
  }

  ngOnInit() {
    this.initThemeSubscriptions()
    this.applyPlatformSetting()
    this.currentUser?.language && this.translate.use(this.currentUser.language)
    this.authService.isLoggedIn$.pipe(filter(Boolean), first()).subscribe(() => this.initializeCrisp())
  }

  toggleSidebar() {
    this.sidebarService.getSidebar().toggleOpen()
  }

  private initThemeSubscriptions() {
    this.authService.isLoggedIn$
      .pipe(
        skip(1),
        filter(Boolean),
        switchMap(() => this.themeService.applyTheme()),
        takeUntil(this.destroyService),
      )
      .subscribe()

    this.authService.isLoggedIn$.pipe(filter(negativeBoolean)).subscribe(() => this.themeService.discardTheme())

    this.themeService.currentTheme$.pipe(filter(Boolean)).subscribe(({ logoSrc }: Theme) => (this.logoSrc = logoSrc))
    this.platformSettings$ = this.platformSettingsService.platformSettings$
  }

  private applyPlatformSetting(): void {
    this.platformSettingsService
      .loadPlatformSettings()
      .pipe(
        switchMap(() => this.platformSettingsService.platformSettings$),
        takeUntil(this.destroyService),
      )
      .subscribe((settings: PlatformSettings) => {
        const { faviconUrl, productName } = settings
        this.favIcon.href = faviconUrl
        this.titleService.setTitle(productName)
      })
  }

  initializeCrisp(): void {
    this.authService.fetchCrispCompanyStats().subscribe({
      next: (response: { data: unknown }) => {
        this.crispService.initCrisp(response.data)
      },
      error: () => {
        // console.error('Error fetching company stats for Crisp:', err);
      },
    })
  }
}
